import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Weekday } from '@frontend/api-schedule-availability';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text, TimeRangeField, useForm } from '@frontend/design-system';
import { WeekDayName } from '../constants';

interface BreakTimeRangeFieldProps {
  day: Weekday;
  index: number;
  joiningText?: string;
  value: string[];
  onChange: (value: string[]) => void;
  minTime?: string;
  maxTime?: string;
  errorMessage?: string;
}

export const BreakTimeRangeField = ({
  day,
  joiningText,
  index,
  value,
  onChange,
  minTime,
  maxTime,
  errorMessage,
}: BreakTimeRangeFieldProps) => {
  const { t } = useTranslation('schedule');
  const [isFocused, setIsFocused] = useState(false);
  const fieldName = `${day}-break-time-range-${index}`;

  // Note: we need to use `useForm` as useControlledField or useFormField is not able to handle this timeRange field
  // particularly when user type in the input field.
  const breakFieldForm = useForm({
    fields: {
      [fieldName]: {
        type: 'timeRange',
        value,
        interval: 15,
        minTime,
        maxTime,
      },
    },
  });

  useEffect(() => {
    const updatedBreak = breakFieldForm.values[fieldName] ?? [];
    if (!isFocused) {
      onChange(updatedBreak ?? []);
    }
  }, [JSON.stringify(breakFieldForm.values[fieldName]), isFocused]);

  return (
    <>
      <TimeRangeField
        startLabel=''
        endLabel=''
        label={t('{{day}} Break Time Range', { day: WeekDayName[day] })}
        joiningText={joiningText || t('to')}
        minTime={minTime}
        maxTime={maxTime}
        css={timeRangeFieldStyle}
        {...breakFieldForm.getFieldProps(fieldName)}
        hasError={!!errorMessage}
        onFocus={(e) => {
          breakFieldForm.getFieldProps(fieldName).onFocus(e);
          setIsFocused(true);
        }}
        onBlur={(e) => {
          breakFieldForm.getFieldProps(fieldName).onBlur(e);
          setIsFocused(false);
        }}
      />
      {!!errorMessage && (
        <Text size='small' color='error' css={css({ margin: theme.spacing(1) })}>
          {errorMessage}
        </Text>
      )}
    </>
  );
};

const timeRangeFieldStyle = css({
  gap: theme.spacing(1),
  'input[type=text]': {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.small,
  },
  '@container (max-width: 700px)': {
    gap: theme.spacing(0),
  },
});
